import { useToast } from '@components/toast/Toast'
import { DocusealForm } from '@docuseal/react'
import { useContract } from '@services/contracts/mutations/useContract'
import { contractsQueryKey } from '@services/contracts/queires/useContracts'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheck, Clock, Download, FileText, PenLine } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ContractList } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export interface ContractWidgetProps {
  contract: ContractList | undefined
}

export function ContractWidget({ contract }: ContractWidgetProps) {
  const { t } = useTranslation('offers')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const toast = useToast()
  const { mutate: getContract } = useContract()

  if (!contract?.id) return null

  const isSigned = contract.status === 'completed'
  // sign_url is null until the contract has a DocuSeal submission. Without it
  // the embedded form has nothing to load and the dialog renders blank.
  const canSign = Boolean(contract.sign_url && contract.email)

  return (
    <Card className="flex h-full flex-col gap-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </span>
          <span className="font-display text-base font-bold text-ink">
            {t('contractWidget.contract')}
          </span>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
            isSigned
              ? 'bg-primary/15 text-primary'
              : 'bg-peach/30 text-warning'
          )}
        >
          {isSigned ? (
            <CircleCheck className="h-3.5 w-3.5" />
          ) : (
            <Clock className="h-3.5 w-3.5" />
          )}
          {isSigned
            ? t('contractWidget.signed')
            : t('contractWidget.created')}
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-ink/[0.03] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <FileText className="h-5 w-5 shrink-0 text-ink/50" />
          <span className="truncate text-sm font-semibold text-ink">
            {contract.contract_number}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!isSigned && (
            <Button size="sm" onClick={() => setIsModalOpen(true)}>
              <PenLine className="h-4 w-4" />
              {t('contractWidget.sign')}
            </Button>
          )}
          <Button
            variant="ghostDark"
            size="sm"
            onClick={() =>
              getContract({ id: contract.id, params: { type: 'offer' } })
            }
          >
            <Download className="h-4 w-4" />
            {t('contractWidget.download')}
          </Button>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('contractWidget.contract')}</DialogTitle>
          </DialogHeader>
          {!isSigned &&
            (canSign ? (
              <DocusealForm
                host={contract.docuseal_cdn_host}
                src={contract.sign_url as string}
                email={contract.email}
                onComplete={async () => {
                  setIsModalOpen(false)
                  await queryClient.invalidateQueries({
                    queryKey: contractsQueryKey
                  })
                  toast.success({ message: t('contractWidget.contractSigned') })
                }}
                minimize
              />
            ) : (
              <p className="m-0 py-6 text-center text-sm text-muted-foreground">
                {t('contractWidget.signingUnavailable')}
              </p>
            ))}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
